import React, { useState, useMemo, useCallback, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../config/api';
import {
  HelpCircle,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Trophy,
  ChevronRight,
  Sparkles,
  Clock,
  Zap,
  Brain
} from 'lucide-react';
import { toast } from 'sonner';
const API = `${API_BASE}/api`;

// Generate quiz questions from JSON data
function generateQuestionsFromJSON(data, maxQuestions = 10) {
  const questions = [];
  
  // Handle array of objects (most common case for tabular data)
  if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object') {
    const items = data;
    const keys = Object.keys(items[0]).filter(k => 
      typeof items[0][k] !== 'object' || items[0][k] === null
    );
    
    // Find a good identifier key (id, name, title, etc.)
    const identifierKey = keys.find(k => 
      ['name', 'title', 'id', 'label', 'username', 'email'].includes(k.toLowerCase())
    ) || keys[0];
    
    // Generate questions about specific items
    items.slice(0, 5).forEach((item, idx) => {
      const identifier = item[identifierKey];
      
      // Question about a specific property value
      keys.forEach(key => {
        if (key !== identifierKey && questions.length < maxQuestions) {
          const value = item[key];
          if (value !== null && value !== undefined) {
            // Create wrong answers from other items
            const wrongAnswers = items
              .filter((_, i) => i !== idx)
              .map(other => other[key])
              .filter(v => v !== value && v !== null && v !== undefined)
              .slice(0, 3);
            
            if (wrongAnswers.length >= 2) {
              const options = shuffleArray([
                { text: formatAnswer(value), isCorrect: true },
                ...wrongAnswers.slice(0, 3).map(w => ({ text: formatAnswer(w), isCorrect: false }))
              ]);
              
              questions.push({
                id: `q_${idx}_${key}`,
                type: 'mcq',
                question: `What is the ${formatKey(key)} for "${identifier}"?`,
                options,
                explanation: `The ${formatKey(key)} for "${identifier}" is ${formatAnswer(value)}.`
              });
            }
          }
        }
      });
      
      // True/False questions
      keys.forEach(key => {
        if (typeof item[key] === 'boolean' && questions.length < maxQuestions) {
          const value = item[key];
          questions.push({
            id: `q_tf_${idx}_${key}`,
            type: 'true_false',
            question: `"${identifier}" has ${formatKey(key)} set to true.`,
            correctAnswer: value,
            explanation: `${identifier}'s ${formatKey(key)} is ${value}.`
          });
        }
      });
    });
    
    // Count/aggregate questions
    if (questions.length < maxQuestions) {
      questions.push({
        id: 'q_count',
        type: 'mcq',
        question: `How many items are in this dataset?`,
        options: shuffleArray([
          { text: String(items.length), isCorrect: true },
          { text: String(items.length + 2), isCorrect: false },
          { text: String(Math.max(1, items.length - 2)), isCorrect: false },
          { text: String(items.length + 5), isCorrect: false }
        ]),
        explanation: `The dataset contains exactly ${items.length} items.`
      });
    }
    
    // Unique value questions
    keys.forEach(key => {
      if (questions.length < maxQuestions) {
        const uniqueValues = [...new Set(items.map(i => i[key]))].filter(v => v != null);
        if (uniqueValues.length > 1 && uniqueValues.length <= 5) {
          const randomValue = uniqueValues[Math.floor(Math.random() * uniqueValues.length)];
          const countWithValue = items.filter(i => i[key] === randomValue).length;
          
          if (countWithValue > 0 && countWithValue < items.length) {
            questions.push({
              id: `q_filter_${key}`,
              type: 'mcq',
              question: `How many items have ${formatKey(key)} equal to "${formatAnswer(randomValue)}"?`,
              options: shuffleArray([
                { text: String(countWithValue), isCorrect: true },
                { text: String(Math.max(1, countWithValue - 1)), isCorrect: false },
                { text: String(countWithValue + 1), isCorrect: false },
                { text: String(countWithValue + 2), isCorrect: false }
              ]),
              explanation: `There are ${countWithValue} items with ${formatKey(key)} equal to "${formatAnswer(randomValue)}".`
            });
          }
        }
      }
    });
  }
  // Handle single object
  else if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
    const keys = Object.keys(data).filter(k => 
      typeof data[k] !== 'object' || data[k] === null
    );
    
    keys.forEach((key, idx) => {
      if (questions.length < maxQuestions) {
        const value = data[key];
        
        if (typeof value === 'boolean') {
          questions.push({
            id: `q_obj_${idx}`,
            type: 'true_false',
            question: `The "${formatKey(key)}" property is set to true.`,
            correctAnswer: value,
            explanation: `The ${formatKey(key)} property has value: ${value}.`
          });
        } else if (value !== null && value !== undefined) {
          questions.push({
            id: `q_obj_${idx}`,
            type: 'mcq',
            question: `What is the value of "${formatKey(key)}"?`,
            options: shuffleArray([
              { text: formatAnswer(value), isCorrect: true },
              { text: 'undefined', isCorrect: false },
              { text: 'null', isCorrect: false },
              { text: typeof value === 'number' ? String(value + 10) : 'N/A', isCorrect: false }
            ]),
            explanation: `The ${formatKey(key)} property is "${formatAnswer(value)}".`
          });
        }
      }
    });
  }
  
  return shuffleArray(questions).slice(0, maxQuestions);
}

// Helper functions
function shuffleArray(array) {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

function formatKey(key) {
  return key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .toLowerCase()
    .trim();
}

function formatAnswer(value) {
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number') return String(value);
  return String(value);
}

export default function JSONQuiz({ jsonData, onComplete, token }) {
  const [quizMode, setQuizMode] = useState(null); // null = selection, 'basic' or 'ai'
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [answered, setAnswered] = useState([]);
  const [quizComplete, setQuizComplete] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loadingAI, setLoadingAI] = useState(false);
  const [aiError, setAiError] = useState(null);
  
  // Generate basic questions
  const basicQuestions = useMemo(() => {
    return generateQuestionsFromJSON(jsonData, 5);
  }, [jsonData]);
  
  // Start quiz with selected mode
  const startQuiz = useCallback(async (mode) => {
    setQuizMode(mode);
    setStartTime(Date.now());
    setAiError(null);
    
    if (mode === 'basic') {
      setQuestions(basicQuestions);
    } else if (mode === 'ai') {
      setLoadingAI(true);
      try {
        const response = await axios.post(`${API}/ai/generate-json-quiz`, {
          json_data: jsonData,
          num_questions: 5
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.questions && response.data.questions.length > 0) {
          setQuestions(response.data.questions);
          toast.success('AI generated quiz questions!');
        } else {
          // Fallback to basic if AI returns empty
          setQuestions(basicQuestions);
          toast.info('Using basic quiz mode');
        }
      } catch (error) {
        console.error('AI quiz generation failed:', error);
        setAiError(error.response?.data?.detail || 'AI generation failed');
        setQuestions(basicQuestions);
        toast.error('AI unavailable, using basic mode');
      } finally {
        setLoadingAI(false);
      }
    }
  }, [jsonData, basicQuestions, token]);
  
  const currentQuestion = questions[currentIndex];
  
  const handleAnswer = useCallback((answer) => {
    if (showResult) return;
    
    setSelectedAnswer(answer);
    setShowResult(true);
    
    let isCorrect = false;
    if (currentQuestion.type === 'mcq') {
      const selectedOption = currentQuestion.options.find(o => o.text === answer);
      isCorrect = selectedOption?.isCorrect;
    } else if (currentQuestion.type === 'true_false') {
      isCorrect = answer === currentQuestion.correctAnswer;
    }
    
    if (isCorrect) {
      setScore(s => s + 1);
    }
    
    setAnswered(prev => [...prev, { questionId: currentQuestion.id, answer, isCorrect }]);
  }, [currentQuestion, showResult]);
  
  const handleNext = useCallback(() => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(i => i + 1);
      setSelectedAnswer(null);
      setShowResult(false);
    } else {
      setQuizComplete(true);
    }
  }, [currentIndex, questions.length]);
  
  const handleRetry = useCallback(() => {
    setCurrentIndex(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setScore(0);
    setAnswered([]);
    setQuizComplete(false);
    setQuizMode(null); // Go back to mode selection
    setQuestions([]);
  }, []);
  
  const timeTaken = useMemo(() => {
    if (!startTime) return '0s';
    const seconds = Math.floor((Date.now() - startTime) / 1000);
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  }, [quizComplete, startTime]);
  
  // Mode Selection Screen
  if (!quizMode) {
    return (
      <div className="p-8 max-w-lg mx-auto">
        <div className="text-center mb-8">
          <Sparkles className="h-12 w-12 text-navigate-gold mx-auto mb-4" />
          <h3 className="text-xl font-heading font-bold text-white mb-2">JSON Data Quiz</h3>
          <p className="text-zinc-400">Test your understanding of the data</p>
        </div>
        
        <div className="space-y-4">
          <button
            onClick={() => startQuiz('basic')}
            className="w-full p-6 bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700 rounded-xl text-left transition-all group"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-zinc-700 rounded-lg flex items-center justify-center shrink-0 group-hover:bg-navigate-gold/20">
                <Zap className="h-6 w-6 text-zinc-400 group-hover:text-navigate-gold" />
              </div>
              <div>
                <h4 className="text-white font-medium mb-1">Basic Quiz</h4>
                <p className="text-sm text-zinc-400">
                  Quick quiz with automatically generated questions based on data values
                </p>
              </div>
            </div>
          </button>
          
          <button
            onClick={() => startQuiz('ai')}
            disabled={loadingAI}
            className="w-full p-6 bg-gradient-to-r from-[#1F3C88]/30 to-blue-900/30 hover:from-[#1F3C88]/50 hover:to-blue-900/50 border border-[#1EBFB3]/30 rounded-xl text-left transition-all group relative overflow-hidden"
          >
            {loadingAI && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-[#1EBFB3] border-t-transparent rounded-full animate-spin" />
              </div>
            )}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#1EBFB3]/20 rounded-lg flex items-center justify-center shrink-0">
                <Brain className="h-6 w-6 text-[#1EBFB3]" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="text-white font-medium">AI-Powered Quiz</h4>
                  <span className="text-xs px-2 py-0.5 bg-[#1EBFB3]/20 text-[#1EBFB3] rounded-full">
                    Claude 4.5
                  </span>
                </div>
                <p className="text-sm text-zinc-400">
                  Sophisticated questions that test analysis, patterns, and deeper understanding
                </p>
              </div>
            </div>
          </button>
        </div>
        
        {aiError && (
          <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400">
            {aiError}
          </div>
        )}
      </div>
    );
  }
  
  // Loading AI questions
  if (loadingAI) {
    return (
      <div className="p-8 text-center">
        <div className="w-16 h-16 border-4 border-[#1EBFB3] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-white font-medium">Generating AI Questions...</p>
        <p className="text-sm text-zinc-400 mt-2">Claude 4.5 is analyzing your data</p>
      </div>
    );
  }
  
  if (questions.length === 0) {
    return (
      <div className="p-8 text-center">
        <HelpCircle className="h-12 w-12 text-zinc-500 mx-auto mb-4" />
        <p className="text-zinc-400">Unable to generate quiz questions from this JSON data.</p>
        <p className="text-sm text-zinc-500 mt-2">The data structure may not be suitable for quiz generation.</p>
      </div>
    );
  }
  
  if (quizComplete) {
    const percentage = Math.round((score / questions.length) * 100);
    const isPassing = percentage >= 70;
    
    return (
      <div className="p-8 max-w-lg mx-auto text-center">
        <div className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 ${
          isPassing ? 'bg-green-500/20' : 'bg-yellow-500/20'
        }`}>
          <Trophy className={`h-12 w-12 ${isPassing ? 'text-green-400' : 'text-yellow-400'}`} />
        </div>
        
        <h3 className="text-2xl font-heading font-bold text-white mb-2">
          {isPassing ? 'Great Job!' : 'Keep Learning!'}
        </h3>
        
        <p className="text-zinc-400 mb-6">
          You scored <span className="text-navigate-gold font-bold">{score}</span> out of <span className="font-bold">{questions.length}</span> questions ({percentage}%)
        </p>
        
        <div className="flex items-center justify-center gap-4 text-sm text-zinc-500 mb-8">
          <div className="flex items-center gap-1">
            <Clock className="h-4 w-4" />
            <span>Time: {timeTaken}</span>
          </div>
          <div className="flex items-center gap-1">
            <CheckCircle2 className="h-4 w-4 text-green-400" />
            <span>{score} correct</span>
          </div>
          <div className="flex items-center gap-1">
            <XCircle className="h-4 w-4 text-red-400" />
            <span>{questions.length - score} incorrect</span>
          </div>
        </div>
        
        <div className="flex justify-center gap-3">
          <button
            onClick={handleRetry}
            className="px-4 py-2 bg-zinc-800 text-white rounded-lg hover:bg-zinc-700 flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Try Again
          </button>
          
          {isPassing && onComplete && (
            <button
              onClick={onComplete}
              className="px-4 py-2 bg-navigate-gold text-black rounded-lg font-medium hover:bg-navigate-gold-light flex items-center gap-2"
            >
              <CheckCircle2 className="h-4 w-4" />
              Mark Complete
            </button>
          )}
        </div>
      </div>
    );
  }
  
  return (
    <div className="p-6 max-w-2xl mx-auto">
      {/* Progress */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-navigate-gold" />
          <span className="text-white font-medium">JSON Data Quiz</span>
        </div>
        <div className="text-sm text-zinc-400">
          Question {currentIndex + 1} of {questions.length}
        </div>
      </div>
      
      {/* Progress Bar */}
      <div className="h-1 bg-zinc-800 rounded-full mb-8 overflow-hidden">
        <div 
          className="h-full bg-navigate-gold transition-all duration-300"
          style={{ width: `${((currentIndex) / questions.length) * 100}%` }}
        />
      </div>
      
      {/* Question */}
      <div className="bg-zinc-800/50 rounded-xl p-6 mb-6">
        <div className="flex items-start gap-3 mb-6">
          <div className="w-8 h-8 bg-navigate-gold/20 rounded-lg flex items-center justify-center shrink-0">
            <HelpCircle className="h-5 w-5 text-navigate-gold" />
          </div>
          <h3 className="text-lg text-white font-medium leading-relaxed">
            {currentQuestion.question}
          </h3>
        </div>
        
        {/* Options */}
        <div className="space-y-3">
          {currentQuestion.type === 'mcq' && currentQuestion.options.map((option, idx) => {
            const isSelected = selectedAnswer === option.text;
            const isCorrect = option.isCorrect;
            
            let bgClass = 'bg-zinc-700/50 hover:bg-zinc-700 border-zinc-700';
            if (showResult) {
              if (isCorrect) {
                bgClass = 'bg-green-500/20 border-green-500';
              } else if (isSelected && !isCorrect) {
                bgClass = 'bg-red-500/20 border-red-500';
              }
            } else if (isSelected) {
              bgClass = 'bg-navigate-gold/20 border-navigate-gold';
            }
            
            return (
              <button
                key={idx}
                onClick={() => handleAnswer(option.text)}
                disabled={showResult}
                className={`w-full p-4 rounded-lg border text-left transition-all flex items-center justify-between ${bgClass} ${
                  showResult ? 'cursor-default' : 'cursor-pointer'
                }`}
              >
                <span className="text-white">{option.text}</span>
                {showResult && isCorrect && <CheckCircle2 className="h-5 w-5 text-green-400" />}
                {showResult && isSelected && !isCorrect && <XCircle className="h-5 w-5 text-red-400" />}
              </button>
            );
          })}
          
          {currentQuestion.type === 'true_false' && (
            <>
              {[true, false].map((value) => {
                const isSelected = selectedAnswer === value;
                const isCorrect = value === currentQuestion.correctAnswer;
                
                let bgClass = 'bg-zinc-700/50 hover:bg-zinc-700 border-zinc-700';
                if (showResult) {
                  if (isCorrect) {
                    bgClass = 'bg-green-500/20 border-green-500';
                  } else if (isSelected && !isCorrect) {
                    bgClass = 'bg-red-500/20 border-red-500';
                  }
                } else if (isSelected) {
                  bgClass = 'bg-navigate-gold/20 border-navigate-gold';
                }
                
                return (
                  <button
                    key={String(value)}
                    onClick={() => handleAnswer(value)}
                    disabled={showResult}
                    className={`w-full p-4 rounded-lg border text-left transition-all flex items-center justify-between ${bgClass} ${
                      showResult ? 'cursor-default' : 'cursor-pointer'
                    }`}
                  >
                    <span className="text-white font-medium">{value ? 'True' : 'False'}</span>
                    {showResult && isCorrect && <CheckCircle2 className="h-5 w-5 text-green-400" />}
                    {showResult && isSelected && !isCorrect && <XCircle className="h-5 w-5 text-red-400" />}
                  </button>
                );
              })}
            </>
          )}
        </div>
        
        {/* Explanation */}
        {showResult && currentQuestion.explanation && (
          <div className="mt-4 p-4 bg-zinc-900 rounded-lg border border-zinc-700">
            <p className="text-sm text-zinc-300">
              <span className="text-navigate-gold font-medium">Explanation: </span>
              {currentQuestion.explanation}
            </p>
          </div>
        )}
      </div>
      
      {/* Next Button */}
      {showResult && (
        <div className="flex justify-end">
          <button
            onClick={handleNext}
            className="px-6 py-2 bg-navigate-gold text-black rounded-lg font-medium hover:bg-navigate-gold-light flex items-center gap-2"
          >
            {currentIndex < questions.length - 1 ? 'Next Question' : 'See Results'}
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}
